// frontend/src/components/import-export/ImportExportDialog.tsx
// Reusable import/export dialog with comparison, conflict resolution, and selection
// Generic enough to be used for recipes, resources, or any entity type

import { useState, useMemo, useCallback } from "react";
import {
  X,
  Download,
  Upload,
  Search,
  Check,
  AlertTriangle,
  SkipForward,
  Plus,
  RefreshCw,
  ArrowLeftRight,
  ChevronDown,
  ChevronUp,
  FileDown,
  FileUp,
  Trash2,
  Copy,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
} from "lucide-react";
import { IconButton, Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

// =============================================================================
// Types
// =============================================================================

export type ImportItemStatus = "new" | "skip" | "conflict" | "update" | "duplicate";

/** Validation error returned by validateImportData */
export interface ImportValidationError {
  /** "error" blocks the import, "warning" allows proceeding */
  severity: "error" | "warning";
  message: string;
}

export interface ImportComparisonField {
  label: string;
  key: string;
  format?: (value: any) => string;
  /** Custom renderer for conflict diff. When provided, replaces the default two-column text comparison for this field. */
  renderDiff?: (incoming: any, existing: any) => React.ReactNode;
}

export interface ImportItem<T> {
  /** Unique row identifier (stable across renders, used for expand/select state) */
  rowId: string;
  data: T;
  status: ImportItemStatus;
  selected: boolean;
  /** For conflicts: the existing item to compare against */
  existing?: T;
  /** For conflicts: which version to keep ('incoming' | 'existing') */
  resolution?: "incoming" | "existing";
  /** Number of times this key appears in the import file (1 = unique, 2+ = has duplicates) */
  groupSize: number;
  /** 0-based position of this occurrence within the duplicate group, in file order */
  groupIndex: number;
  /** What the row's status would be if it were chosen as the primary occurrence in its group */
  primaryStatus: Exclude<ImportItemStatus, "duplicate">;
  /** True if this row is currently the primary occurrence (i.e., selected for import); false → "duplicate" */
  isPrimary: boolean;
}

export interface ImportExportConfig<T> {
  /** Entity name (singular) for display, e.g. "recette" */
  entityName: string;
  /** Entity name (plural) for display, e.g. "recettes" */
  entityNamePlural: string;
  /** Unique key extractor, e.g. (item) => item.name */
  getKey: (item: T) => string;
  /** Display name extractor */
  getDisplayName: (item: T) => string;
  /** Category/group extractor (optional) */
  getCategory?: (item: T) => string;
  /** Fields to show in comparison view */
  comparisonFields: ImportComparisonField[];
  /** Check if two items are identical (skip vs conflict) */
  isIdentical: (a: T, b: T) => boolean;
  /** Optional: when existing item found and not identical, check if it's a simple update
   *  (e.g., existing has empty recipe, import fills it in). Returns true → "update" status
   *  instead of "conflict". Updates are auto-selected and don't need conflict resolution. */
  isUpdate?: (incoming: T, existing: T) => boolean;
  /** Optional: render a detail/preview panel for an item (e.g., ingredient list).
   *  Shown when the user expands a row. */
  renderDetail?: (item: T) => React.ReactNode;
  /** Optional: render a one-line compact summary of an item (e.g., "4 ingrédients : Pâte, Sauce…").
   *  Used in the duplicate compare panel as the default collapsed view, with renderDetail
   *  available behind a chevron toggle. Falls back to renderDetail when not provided. */
  renderSummary?: (item: T) => React.ReactNode;
  /** Optional: render a mid-density view (icons grouped by sub-type, names visible) used as
   *  the default body in the duplicate compare panel. More visual than renderSummary,
   *  more compact than renderDetail. Falls back to renderSummary when not provided. */
  renderMedium?: (item: T) => React.ReactNode;
  /** Optional: extract an ISO date string from an item (e.g., createdAt). Used to display
   *  a date tag next to the existing version in conflict comparison. */
  getItemDate?: (item: T) => string | undefined;
  /** File extension for export */
  fileExtension?: string;
}

interface ImportExportDialogProps<T> {
  isOpen: boolean;
  onClose: () => void;
  config: ImportExportConfig<T>;
  /** Current items in the system (for comparison during import) */
  existingItems: T[];
  /** Called when user confirms export */
  onExport: (items: T[]) => void;
  /** Called when user confirms import with resolved items.
   *  Optionally accepts a progress callback and returns an ImportResult for partial failure reporting. */
  onImport: (
    items: { data: T; resolution?: "incoming" | "existing" }[],
    onProgress?: (current: number, total: number) => void,
  ) => Promise<ImportResult | void> | void;
  /** Optional custom parser for import data (handles different formats) */
  parseImportData?: (parsed: any) => T[];
  /** Optional validator — called with raw parsed JSON before parsing.
   *  Return errors/warnings to block or warn about the import. */
  validateImportData?: (parsed: any) => ImportValidationError[];
  /** Optional reset handler — when provided, shows a "reset data" link in the upload screen.
   *  Should return true if reset was successful, false/throw if cancelled or failed. */
  onReset?: () => Promise<boolean>;
  /** Label for the reset link (default: "Réinitialiser les données avant l'import") */
  resetLabel?: string;
  /** Optional extra controls rendered inside the export step (above the action buttons).
   *  Use this to expose entity-specific export toggles like "Inclure le catalogue". */
  exportOptions?: React.ReactNode;
}

/** Result of an import operation — supports partial failure reporting */
export interface ImportResult {
  succeeded: number;
  failed: { name: string; error: string }[];
  /** Names of resources auto-created by the backend because they were referenced
   *  by an imported recipe but didn't yet exist (cost defaults to 0; user fills in later). */
  autoCreatedResources?: string[];
  /** Names of suppliers auto-created by the backend during a supplier-resource link
   *  import. Same UX role as autoCreatedResources, displayed alongside it. */
  autoCreatedSuppliers?: string[];
}

/** Resolve a dot-separated key path like "menuItem.name" on an object */
function getNestedValue(obj: any, keyPath: string): any {
  return keyPath.split('.').reduce((current, key) => current?.[key], obj);
}

/** Format an ISO date string as YYYY-MM-DD (locale-neutral, French-friendly) */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toISOString().split("T")[0];
  } catch {
    return "—";
  }
}

type DialogMode = "choose" | "export" | "import-upload" | "import-review" | "import-error" | "import-confirm" | "import-progress" | "import-result";

// =============================================================================
// Component
// =============================================================================

export function ImportExportDialog<T>({
  isOpen,
  onClose,
  config,
  existingItems,
  onExport,
  onImport,
  parseImportData,
  validateImportData,
  onReset,
  resetLabel,
  exportOptions,
}: ImportExportDialogProps<T>) {
  const [mode, setMode] = useState<DialogMode>("choose");
  const [importItems, setImportItems] = useState<ImportItem<T>[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<ImportItemStatus | "all">("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ImportValidationError[]>([]);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  /** ISO date string from the import file's `exportedAt` field, if present */
  const [importedAt, setImportedAt] = useState<string | null>(null);
  /** Group keys (lowercased) that the user has confirmed — non-primary duplicates of these
   *  are hidden from the list and excluded from counts, giving a sense of progress. */
  const [resolvedGroupKeys, setResolvedGroupKeys] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const handleClose = () => {
    setMode("choose");
    setImportItems([]);
    setSearchTerm("");
    setFilterStatus("all");
    setFilterCategory("all");
    setExpandedId(null);
    setValidationErrors([]);
    setResetConfirmOpen(false);
    setResetting(false);
    setResetDone(false);
    setImportProgress(0);
    setImportTotal(0);
    setImportResult(null);
    setImportedAt(null);
    setResolvedGroupKeys(new Set());
    onClose();
  };

  const handleExport = () => {
    onExport(existingItems);
    handleClose();
  };

  const processFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const raw = event.target?.result as string;
        if (!raw || raw.trim().length === 0) {
          setValidationErrors([{ severity: "error", message: "Le fichier est vide." }]);
          setMode("import-error");
          return;
        }

        let parsed: any;
        try {
          parsed = JSON.parse(raw);
        } catch {
          setValidationErrors([{ severity: "error", message: "Le fichier n'est pas un JSON valide. Vérifiez que le fichier n'est pas corrompu." }]);
          setMode("import-error");
          return;
        }

        // Run custom validation if provided
        if (validateImportData) {
          const errors = validateImportData(parsed);
          if (errors.length > 0) {
            const hasBlockingError = errors.some(e => e.severity === "error");
            setValidationErrors(errors);
            if (hasBlockingError) {
              setMode("import-error");
              return;
            }
            // Warnings only — proceed but keep them visible
          }
        }

        const items: T[] = parseImportData
          ? parseImportData(parsed)
          : Array.isArray(parsed) ? parsed : parsed.data || [];

        if (items.length === 0) {
          setValidationErrors([{ severity: "error", message: "Aucun élément trouvé dans le fichier. Vérifiez le format et la structure du fichier." }]);
          setMode("import-error");
          return;
        }

        // Extract export date from the file wrapper (if present)
        const exportedAt = !Array.isArray(parsed) && typeof parsed?.exportedAt === "string"
          ? parsed.exportedAt
          : null;
        setImportedAt(exportedAt);

        processImportItems(items);
        setMode("import-review");
      } catch (err) {
        setValidationErrors([{ severity: "error", message: "Erreur inattendue lors de la lecture du fichier." }]);
        setMode("import-error");
      }
    };
    reader.readAsText(file);
  }, [parseImportData, validateImportData]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.json')) {
      processFile(file);
    } else {
      toast.error("Veuillez déposer un fichier JSON");
    }
  }, [processFile]);

  const processImportItems = (incoming: T[]) => {
    const existingMap = new Map<string, T>();
    existingItems.forEach((item) => {
      existingMap.set(config.getKey(item).toLowerCase(), item);
    });

    // Count occurrences per key (for groupSize) and track running index per key
    const keyCounts = new Map<string, number>();
    incoming.forEach((data) => {
      const key = config.getKey(data).toLowerCase();
      keyCounts.set(key, (keyCounts.get(key) ?? 0) + 1);
    });

    const seenKeys = new Map<string, number>(); // key → next groupIndex

    const processed: ImportItem<T>[] = incoming.map((data, idx) => {
      const key = config.getKey(data).toLowerCase();
      const groupSize = keyCounts.get(key) ?? 1;
      const groupIndex = seenKeys.get(key) ?? 0;
      seenKeys.set(key, groupIndex + 1);
      const isPrimary = groupIndex === 0;
      const rowId = `${key}#${groupIndex}#${idx}`;

      const existing = existingMap.get(key);
      // Compute primaryStatus — what status this row would have if it were the chosen primary
      let primaryStatus: Exclude<ImportItemStatus, "duplicate">;
      let resolution: "incoming" | "existing" | undefined;
      if (!existing) {
        primaryStatus = "new";
      } else if (config.isIdentical(data, existing)) {
        primaryStatus = "skip";
      } else if (config.isUpdate?.(data, existing)) {
        primaryStatus = "update";
        resolution = "incoming";
      } else {
        primaryStatus = "conflict";
        resolution = "incoming";
      }

      const status: ImportItemStatus = isPrimary ? primaryStatus : "duplicate";
      const selected = isPrimary && primaryStatus !== "skip";

      return {
        rowId,
        data,
        status,
        selected,
        existing,
        resolution,
        groupSize,
        groupIndex,
        primaryStatus,
        isPrimary,
      };
    });

    setImportItems(processed);
  };

  const counts = useMemo(() => {
    const c = { new: 0, skip: 0, conflict: 0, update: 0, duplicate: 0, selected: 0, resolved: 0 };
    // Track duplicate groups to count groups (not individual items).
    const dupGroups = new Set<string>();
    const resolvedGroups = new Set<string>();

    importItems.forEach((item) => {
      const groupKey = config.getKey(item.data).toLowerCase();

      // Non-primary duplicates: count groups, not individual items.
      if (item.status === "duplicate") {
        if (resolvedGroupKeys.has(groupKey)) {
          resolvedGroups.add(groupKey);
        } else {
          dupGroups.add(groupKey);
        }
        return; // non-primary duplicates don't appear in list → don't count in "selected"
      }

      c[item.status]++;
      if (item.selected) c.selected++;
    });

    c.duplicate = dupGroups.size;   // number of unresolved groups
    c.resolved = resolvedGroups.size; // number of resolved groups
    return c;
  }, [importItems, resolvedGroupKeys, config]);

  // Categories available in import items (for category filter)
  const categories = useMemo(() =>
    [...new Set(importItems.map(i => config.getCategory?.(i.data) || "").filter(Boolean))].sort(),
    [importItems, config]
  );

  const filteredItems = useMemo(() => {
    return importItems.filter((item) => {
      // Always keep the currently expanded row visible — its status may change
      // mid-interaction (e.g., user picks a new primary while filtered to "Doublon"),
      // and removing the row would collapse the panel before the user can click "Confirmer".
      if (item.rowId === expandedId) return true;

      // Non-primary duplicates are always hidden — only the primary row for each
      // group appears in the list, with a "Comparer (N)" button to access siblings.
      if (item.status === "duplicate") return false;

      // "Doublon" filter: show primary rows that have duplicate siblings.
      if (filterStatus === "duplicate") {
        return item.groupSize > 1 && item.isPrimary;
      }
      if (filterStatus !== "all" && item.status !== filterStatus) return false;
      if (filterCategory !== "all") {
        const cat = config.getCategory?.(item.data) || "";
        if (cat !== filterCategory) return false;
      }
      if (searchTerm) {
        const name = config.getDisplayName(item.data).toLowerCase();
        const cat = config.getCategory?.(item.data)?.toLowerCase() || "";
        const term = searchTerm.toLowerCase();
        if (!name.includes(term) && !cat.includes(term)) return false;
      }
      return true;
    });
  }, [importItems, filterStatus, filterCategory, searchTerm, config, expandedId]);

  const toggleItem = (rowId: string) => {
    setImportItems((prev) =>
      prev.map((item) =>
        item.rowId === rowId ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const toggleAll = (selected: boolean) => {
    setImportItems((prev) =>
      prev.map((item) =>
        item.status === "skip" || item.status === "duplicate" ? item : { ...item, selected }
      )
    );
  };

  const setResolution = (rowId: string, resolution: "incoming" | "existing") => {
    setImportItems((prev) =>
      prev.map((item) =>
        item.rowId === rowId ? { ...item, resolution } : item
      )
    );
  };

  /** Mark a duplicate group as resolved → hides non-primary occurrences from the list */
  const confirmGroupResolution = (groupKey: string) => {
    setResolvedGroupKeys((prev) => {
      const next = new Set(prev);
      next.add(groupKey);
      return next;
    });
    setExpandedId(null); // collapse the panel for visual feedback
  };

  /** Re-show a previously resolved group's duplicates */
  const undoGroupResolution = (groupKey: string) => {
    setResolvedGroupKeys((prev) => {
      const next = new Set(prev);
      next.delete(groupKey);
      return next;
    });
  };

  /** Swap which occurrence in a duplicate group is the primary (selected) one */
  const swapPrimary = (rowId: string) => {
    setImportItems((prev) => {
      const target = prev.find((i) => i.rowId === rowId);
      if (!target || target.groupSize <= 1) return prev;
      const groupKey = config.getKey(target.data).toLowerCase();
      return prev.map((item) => {
        const itemKey = config.getKey(item.data).toLowerCase();
        if (itemKey !== groupKey) return item;
        if (item.rowId === rowId) {
          return {
            ...item,
            isPrimary: true,
            status: item.primaryStatus,
            selected: item.primaryStatus !== "skip",
          };
        }
        return {
          ...item,
          isPrimary: false,
          status: "duplicate" as const,
          selected: false,
        };
      });
    });
  };

  // Items that will actually be imported (selected, not skipped/duplicated)
  const itemsToImport = useMemo(() =>
    importItems
      .filter((item) => item.selected && item.status !== "skip" && item.status !== "duplicate")
      .map((item) => ({ data: item.data, resolution: item.resolution })),
    [importItems]
  );

  // Summary counts for confirmation screen
  const confirmSummary = useMemo(() => {
    const s = { new: 0, update: 0, conflict_incoming: 0, conflict_existing: 0 };
    importItems.filter(i => i.selected && i.status !== "skip" && i.status !== "duplicate").forEach(item => {
      if (item.status === "new") s.new++;
      else if (item.status === "update") s.update++;
      else if (item.status === "conflict") {
        if (item.resolution === "incoming") s.conflict_incoming++;
        else s.conflict_existing++;
      }
    });
    return s;
  }, [importItems]);

  // Category breakdown for confirm screen — count selected-to-import items per category,
  // sorted descending by count so the biggest categories appear first.
  const confirmCategoryBreakdown = useMemo(() => {
    if (!config.getCategory) return [] as { category: string; count: number }[];
    const cats = new Map<string, number>();
    itemsToImport.forEach(({ data }) => {
      const cat = config.getCategory!(data) || "Sans catégorie";
      cats.set(cat, (cats.get(cat) ?? 0) + 1);
    });
    return [...cats.entries()]
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
  }, [itemsToImport, config]);

  // Preview names for confirm screen — first few display names so the user knows what
  // they're about to import without scrolling through the full list.
  const confirmPreviewNames = useMemo(() => {
    return itemsToImport.slice(0, 5).map(({ data }) => config.getDisplayName(data));
  }, [itemsToImport, config]);

  const handleConfirmImport = () => {
    // Transition to confirmation step instead of importing directly
    setMode("import-confirm");
  };

  const handleExecuteImport = async () => {
    setMode("import-progress");
    setImportProgress(0);
    setImportTotal(itemsToImport.length);

    try {
      const result = await onImport(
        itemsToImport,
        (current, total) => {
          setImportProgress(current);
          setImportTotal(total);
        },
      );

      if (result) {
        setImportResult(result);
        setMode("import-result");
      } else {
        // No result returned — legacy behavior, just close
        handleClose();
      }
    } catch (err) {
      setImportResult({
        succeeded: 0,
        failed: [{ name: "Import", error: String(err) }],
      });
      setMode("import-result");
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-[var(--card)] rounded-xl shadow-xl border border-[var(--border)] w-full animate-in fade-in zoom-in-95 duration-200"
        style={{ maxWidth: mode === "import-review" || mode === "import-confirm" ? "56rem" : "28rem", maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              mode === "import-error" ? "bg-[var(--color-brick-bg)]" :
              mode === "import-result" ? (importResult && importResult.failed.length > 0 ? "bg-[var(--color-amber-bg)]" : "bg-[var(--color-olive-bg)]") :
              mode === "export" ? "bg-[var(--color-olive-bg)]" :
              mode.startsWith("import") ? "bg-[var(--color-steel-bg)]" :
              "bg-[var(--color-steel-bg)]"
            }`}>
              {mode === "import-error" ? (
                <AlertTriangle className="w-5 h-5 text-[var(--color-brick)]" />
              ) : mode === "import-result" ? (
                importResult && importResult.failed.length > 0
                  ? <AlertTriangle className="w-5 h-5 text-[var(--color-amber)]" />
                  : <CheckCircle className="w-5 h-5 text-[var(--color-olive)]" />
              ) : mode === "export" ? (
                <FileDown className={`w-5 h-5 text-[var(--color-olive)]`} />
              ) : mode.startsWith("import") ? (
                <FileUp className="w-5 h-5 text-[var(--color-steel)]" />
              ) : (
                <ArrowLeftRight className="w-5 h-5 text-[var(--color-steel)]" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--foreground)]">
                {mode === "choose" && `Importer / Exporter`}
                {mode === "export" && `Exporter les ${config.entityNamePlural}`}
                {mode === "import-upload" && `Importer des ${config.entityNamePlural}`}
                {mode === "import-review" && `Vérifier l'importation`}
                {mode === "import-error" && `Fichier incompatible`}
                {mode === "import-confirm" && `Confirmer l'importation`}
                {mode === "import-progress" && `Importation en cours...`}
                {mode === "import-result" && `Résultat de l'importation`}
              </h2>
              {mode === "import-review" && (
                <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
                  {importItems.length} {config.entityNamePlural} trouvé(e)s dans le fichier
                  {counts.duplicate > 0 && ` (${counts.duplicate} groupe${counts.duplicate > 1 ? "s" : ""} de doublons)`}
                </p>
              )}
            </div>
          </div>
          <IconButton icon={X} variant="ghost" onClick={handleClose} />
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 140px)" }}>
          {/* ── Mode: Choose ── */}
          {mode === "choose" && (
            <div className="p-5 space-y-3">
              <button
                onClick={() => setMode("export")}
                className="w-full p-4 bg-[var(--muted)] hover:bg-[var(--switch-background)] border border-[var(--switch-background)] rounded-lg transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[var(--color-olive-bg)] rounded-lg group-hover:bg-[var(--color-olive)]/30 transition-colors">
                    <Download className="w-5 h-5 text-[var(--color-olive)]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[var(--foreground)]">
                      Exporter
                    </div>
                    <div className="text-sm text-[var(--muted-foreground)]">
                      Sauvegarder {existingItems.length} {config.entityNamePlural} en fichier JSON
                    </div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setMode("import-upload")}
                className="w-full p-4 bg-[var(--muted)] hover:bg-[var(--switch-background)] border border-[var(--switch-background)] rounded-lg transition-colors text-left group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[var(--color-steel-bg)] rounded-lg group-hover:bg-[var(--color-steel)]/30 transition-colors">
                    <Upload className="w-5 h-5 text-[var(--color-steel)]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-[var(--foreground)]">
                      Importer
                    </div>
                    <div className="text-sm text-[var(--muted-foreground)]">
                      Charger des {config.entityNamePlural} depuis un fichier JSON
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* ── Mode: Export ── */}
          {mode === "export" && (
            <div className="p-5 space-y-4">
              <p className="text-sm text-[var(--muted-foreground)]">
                {existingItems.length} {config.entityNamePlural} seront exporté(e)s au format JSON.
                Ce fichier pourra être importé dans un autre établissement.
              </p>
              {exportOptions}
              <div className="flex gap-3">
                <Button variant="secondary" size="sm" onClick={() => setMode("choose")} className="flex-1">
                  Retour
                </Button>
                <Button variant="primary" size="sm" icon={Download} onClick={handleExport} className="flex-1">
                  Télécharger le fichier
                </Button>
              </div>
            </div>
          )}

          {/* ── Mode: Import Upload ── */}
          {mode === "import-upload" && (
            <div className="p-5 space-y-4">
              <p className="text-sm text-[var(--muted-foreground)]">
                Sélectionnez un fichier JSON contenant les {config.entityNamePlural} à importer.
                Les doublons seront détectés automatiquement.
              </p>
              <label
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                  isDragOver
                    ? "border-[var(--color-steel)] bg-[var(--color-steel-bg)]/50 scale-[1.02]"
                    : "border-[var(--switch-background)] hover:border-[var(--color-steel)] hover:bg-[var(--color-steel-bg)]/30"
                }`}
              >
                <Upload className={`w-8 h-8 mb-2 transition-colors ${isDragOver ? "text-[var(--color-steel)]" : "text-[var(--muted-foreground)]"}`} />
                <span className={`text-sm transition-colors ${isDragOver ? "text-[var(--color-steel)] font-medium" : "text-[var(--muted-foreground)]"}`}>
                  {isDragOver ? "Déposer le fichier ici" : "Glisser-déposer ou cliquer pour sélectionner un fichier JSON"}
                </span>
                <input
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </label>
              {/* Reset data option */}
              {onReset && !resetConfirmOpen && !resetDone && (
                <button
                  onClick={() => setResetConfirmOpen(true)}
                  className="flex items-center justify-center gap-2 w-full py-2 text-xs text-[var(--color-brick)] hover:text-[var(--color-brick-dark)] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {resetLabel || "Réinitialiser les données avant l'import"}
                </button>
              )}

              {/* Reset confirmation */}
              {resetConfirmOpen && !resetDone && (
                <div className="p-3 rounded-lg border border-[var(--color-brick-border)] bg-[var(--color-brick-bg)]">
                  <p className="text-sm text-[var(--color-brick)] font-medium mb-2">
                    Cette action va supprimer toutes les ressources et recettes existantes. Les articles du menu importés de Veloce seront conservés.
                  </p>
                  <p className="text-xs text-[var(--color-brick)] mb-3 opacity-80">
                    Cette action est irréversible.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setResetConfirmOpen(false)}
                      className="flex-1"
                      disabled={resetting}
                    >
                      Annuler
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      className="flex-1"
                      disabled={resetting}
                      onClick={async () => {
                        setResetting(true);
                        try {
                          const ok = await onReset!();
                          if (ok) {
                            setResetDone(true);
                            setResetConfirmOpen(false);
                          }
                        } catch {
                          // error handled by onReset
                        } finally {
                          setResetting(false);
                        }
                      }}
                    >
                      {resetting ? "Suppression..." : "Confirmer la réinitialisation"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Reset success */}
              {resetDone && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--color-olive-bg)] border border-[var(--color-olive-border)] text-[var(--color-olive)] text-sm">
                  <Check className="w-4 h-4 flex-shrink-0" />
                  Données réinitialisées. Vous pouvez maintenant importer le fichier.
                </div>
              )}

              <Button variant="secondary" size="sm" onClick={() => setMode("choose")} fullWidth>
                Retour
              </Button>
            </div>
          )}

          {/* ── Mode: Import Error ── */}
          {mode === "import-error" && (
            <div className="p-5 space-y-4">
              <div className="space-y-2">
                {validationErrors.map((err, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${
                      err.severity === "error"
                        ? "bg-[var(--color-brick-bg)] border-[var(--color-brick-border)] text-[var(--color-brick)]"
                        : "bg-[var(--color-amber-bg)] border-[var(--color-amber-border)] text-[var(--color-amber)]"
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{err.message}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => { setValidationErrors([]); setMode("import-upload"); }}
                  fullWidth
                >
                  Réessayer avec un autre fichier
                </Button>
              </div>
            </div>
          )}

          {/* ── Mode: Import Review ── */}
          {mode === "import-review" && (
            <div className="flex flex-col">
              {/* Warnings (non-blocking) + duplicate warning + imported-at info */}
              {(validationErrors.filter(e => e.severity === "warning").length > 0 || counts.duplicate > 0 || importedAt) && (
                <div className="px-5 pt-3 space-y-1">
                  {importedAt && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--muted)] border border-[var(--border)] text-[var(--muted-foreground)] text-xs">
                      <FileUp className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Fichier exporté le {formatDate(importedAt)}</span>
                    </div>
                  )}
                  {validationErrors.filter(e => e.severity === "warning").map((err, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-amber-bg)] border border-[var(--color-amber-border)] text-[var(--color-amber)] text-xs">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{err.message}</span>
                    </div>
                  ))}
                  {counts.duplicate > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-amber-bg)] border border-[var(--color-amber-border)] text-[var(--color-amber)] text-xs">
                      <Copy className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>
                        {counts.duplicate} groupe{counts.duplicate > 1 ? "s" : ""} de doublons détecté{counts.duplicate > 1 ? "s" : ""}
                        {counts.resolved > 0 && (
                          <span className="opacity-75"> · {counts.resolved} résolu{counts.resolved > 1 ? "s" : ""}</span>
                        )}
                        . Cliquez sur « Comparer » pour vérifier ou changer la version sélectionnée.
                      </span>
                    </div>
                  )}
                  {counts.duplicate === 0 && counts.resolved > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-olive-bg)] border border-[var(--color-olive-border)] text-[var(--color-olive)] text-xs">
                      <Check className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>Tous les doublons ont été traités ({counts.resolved} groupe{counts.resolved > 1 ? "s" : ""}).</span>
                    </div>
                  )}
                </div>
              )}

              {/* Summary badges */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)] flex-wrap">
                <FilterBadge
                  label="Tout"
                  count={importItems.length}
                  active={filterStatus === "all"}
                  onClick={() => setFilterStatus("all")}
                  variant="neutral"
                />
                <FilterBadge
                  label="Nouveau"
                  count={counts.new}
                  active={filterStatus === "new"}
                  onClick={() => setFilterStatus("new")}
                  variant="success"
                />
                {counts.update > 0 && (
                  <FilterBadge
                    label="Mise à jour"
                    count={counts.update}
                    active={filterStatus === "update"}
                    onClick={() => setFilterStatus("update")}
                    variant="info"
                  />
                )}
                <FilterBadge
                  label="Conflit"
                  count={counts.conflict}
                  active={filterStatus === "conflict"}
                  onClick={() => setFilterStatus("conflict")}
                  variant="warning"
                />
                <FilterBadge
                  label="Identique"
                  count={counts.skip}
                  active={filterStatus === "skip"}
                  onClick={() => setFilterStatus("skip")}
                  variant="neutral"
                />
                {counts.duplicate > 0 && (
                  <FilterBadge
                    label="Doublon"
                    count={counts.duplicate}
                    active={filterStatus === "duplicate"}
                    onClick={() => setFilterStatus("duplicate")}
                    variant="warning"
                  />
                )}

                <div className="ml-auto flex items-center gap-2">
                  {categories.length > 1 && (
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="px-2 py-1.5 text-xs bg-[var(--input-background)] border border-[var(--switch-background)] rounded-md focus:border-[var(--color-steel)] focus:ring-1 focus:ring-[var(--color-steel-bg)] text-[var(--foreground)]"
                    >
                      <option value="all">Toutes les catégories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  )}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--muted-foreground)]" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-8 pr-3 py-1.5 text-xs bg-[var(--input-background)] border border-[var(--switch-background)] rounded-md w-40 focus:border-[var(--color-steel)] focus:ring-1 focus:ring-[var(--color-steel-bg)] text-[var(--foreground)]"
                    />
                  </div>
                </div>
              </div>

              {/* Select all / none */}
              <div className="flex items-center justify-between px-5 py-2 bg-[var(--muted)]/50 border-b border-[var(--border)]">
                <span className="text-xs text-[var(--muted-foreground)]">
                  {counts.selected} sélectionné(e)s sur {counts.new + counts.update + counts.conflict + counts.skip}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleAll(true)}
                    className="text-xs text-[var(--color-steel)] hover:underline"
                  >
                    Tout sélectionner
                  </button>
                  <span className="text-xs text-[var(--muted-foreground)]">|</span>
                  <button
                    onClick={() => toggleAll(false)}
                    className="text-xs text-[var(--color-steel)] hover:underline"
                  >
                    Tout désélectionner
                  </button>
                </div>
              </div>

              {/* Item list */}
              <div className="overflow-y-auto" style={{ maxHeight: "calc(90vh - 340px)" }}>
                {filteredItems.map((item) => (
                  <ImportItemRow
                    key={item.rowId}
                    item={item}
                    allItems={importItems}
                    config={config}
                    isExpanded={expandedId === item.rowId}
                    onToggleExpand={() =>
                      setExpandedId(expandedId === item.rowId ? null : item.rowId)
                    }
                    onToggleSelect={() => toggleItem(item.rowId)}
                    onSetResolution={(res) => setResolution(item.rowId, res)}
                    onChoosePrimary={swapPrimary}
                    importedAt={importedAt}
                    isGroupResolved={resolvedGroupKeys.has(config.getKey(item.data).toLowerCase())}
                    onConfirmGroup={confirmGroupResolution}
                    onUndoGroup={undoGroupResolution}
                  />
                ))}

                {filteredItems.length === 0 && (
                  <div className="py-10 text-center text-sm text-[var(--muted-foreground)]">
                    Aucun résultat
                  </div>
                )}
              </div>

              {/* Footer actions */}
              <div className="flex items-center justify-between p-4 border-t border-[var(--border)] bg-[var(--background)]">
                <Button variant="secondary" size="sm" onClick={() => setMode("choose")}>
                  Retour
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Upload}
                  onClick={handleConfirmImport}
                  disabled={counts.selected === 0}
                >
                  Importer {counts.selected > 0 ? `(${counts.selected})` : ""}
                </Button>
              </div>
            </div>
          )}

          {/* ── Mode: Import Confirm ── */}
          {mode === "import-confirm" && (
            <div className="flex flex-col" style={{ maxHeight: "calc(90vh - 140px)" }}>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                {/* 1. Big total */}
                <div className="text-center py-3 rounded-lg bg-[var(--muted)]/30 border border-[var(--border)]">
                  <div className="text-4xl font-bold text-[var(--foreground)] tabular-nums">
                    {itemsToImport.length}
                  </div>
                  <div className="text-sm text-[var(--muted-foreground)] mt-1">
                    {config.entityNamePlural} seront importé(e)s
                  </div>
                </div>

                {/* 2. File source line */}
                {importedAt && (
                  <div className="flex items-center gap-2 text-xs text-[var(--muted-foreground)]">
                    <FileUp className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Source : fichier exporté le {formatDate(importedAt)}</span>
                  </div>
                )}

                {/* 3. Breakdown by status */}
                <div>
                  <div className="text-xs font-medium text-[var(--muted-foreground)] mb-1.5 uppercase tracking-wide">
                    Détail
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {confirmSummary.new > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-olive-bg)] border border-[var(--color-olive-border)] text-[var(--color-olive)] text-sm">
                        <Plus className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">{confirmSummary.new}</span>
                        <span className="opacity-90">nouveau{confirmSummary.new > 1 ? "x" : ""}</span>
                      </div>
                    )}
                    {confirmSummary.update > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-steel-bg)] border border-[var(--color-steel-border)] text-[var(--color-steel)] text-sm">
                        <RefreshCw className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">{confirmSummary.update}</span>
                        <span className="opacity-90">mise{confirmSummary.update > 1 ? "s" : ""} à jour</span>
                      </div>
                    )}
                    {confirmSummary.conflict_incoming > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-amber-bg)] border border-[var(--color-amber-border)] text-[var(--color-amber)] text-sm">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium">{confirmSummary.conflict_incoming}</span>
                        <span className="opacity-90">conflit{confirmSummary.conflict_incoming > 1 ? "s" : ""}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. Category breakdown */}
                {confirmCategoryBreakdown.length > 1 && (
                  <div>
                    <div className="text-xs font-medium text-[var(--muted-foreground)] mb-1.5 uppercase tracking-wide">
                      Par catégorie
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {confirmCategoryBreakdown.map(({ category, count }) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[var(--muted)] border border-[var(--border)] text-xs text-[var(--foreground)]"
                        >
                          <span>{category}</span>
                          <span className="text-[var(--muted-foreground)] tabular-nums">({count})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 5. Preview names */}
                {confirmPreviewNames.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-[var(--muted-foreground)] mb-1.5 uppercase tracking-wide">
                      Notamment
                    </div>
                    <div className="text-sm text-[var(--foreground)] leading-relaxed">
                      {confirmPreviewNames.join(", ")}
                      {itemsToImport.length > confirmPreviewNames.length && (
                        <span className="text-[var(--muted-foreground)]">
                          {" "}… et {itemsToImport.length - confirmPreviewNames.length} autre{itemsToImport.length - confirmPreviewNames.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* 6. What's NOT being imported */}
                {(counts.skip > 0 || counts.resolved > 0) && (
                  <div>
                    <div className="text-xs font-medium text-[var(--muted-foreground)] mb-1.5 uppercase tracking-wide">
                      Non importé
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {counts.skip > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[var(--muted)] border border-[var(--border)] text-xs text-[var(--muted-foreground)]">
                          <SkipForward className="w-3 h-3" />
                          {counts.skip} identique{counts.skip > 1 ? "s" : ""} (déjà présent{counts.skip > 1 ? "s" : ""})
                        </span>
                      )}
                      {counts.resolved > 0 && (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-[var(--muted)] border border-[var(--border)] text-xs text-[var(--muted-foreground)]">
                          <Copy className="w-3 h-3" />
                          {counts.resolved} doublon{counts.resolved > 1 ? "s" : ""} résolu{counts.resolved > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* 7. Irreversibility note */}
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-[var(--color-amber-bg)]/40 border border-[var(--color-amber-border)]/50 text-xs text-[var(--color-amber)]">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Cette action ne peut pas être annulée automatiquement.
                    Pensez à exporter vos données actuelles d'abord si nécessaire.
                  </span>
                </div>
              </div>

              {/* Footer (sticky-feeling, outside scroll area) */}
              <div className="flex gap-3 p-4 border-t border-[var(--border)] bg-[var(--background)] flex-shrink-0">
                <Button variant="secondary" size="sm" onClick={() => setMode("import-review")} className="flex-1">
                  Retour
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  icon={Upload}
                  onClick={handleExecuteImport}
                  className="flex-1"
                >
                  Confirmer l'importation ({itemsToImport.length})
                </Button>
              </div>
            </div>
          )}

          {/* ── Mode: Import Progress ── */}
          {mode === "import-progress" && (
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-[var(--color-steel)] animate-spin" />
                <span className="text-sm text-[var(--foreground)]">
                  Importation en cours...
                </span>
              </div>
              <div className="space-y-2">
                <div className="w-full h-2.5 bg-[var(--muted)] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--color-steel)] rounded-full transition-all duration-300"
                    style={{ width: importTotal > 0 ? `${Math.round((importProgress / importTotal) * 100)}%` : "0%" }}
                  />
                </div>
                <p className="text-xs text-[var(--muted-foreground)] text-center">
                  {importTotal > 0
                    ? `${importProgress} / ${importTotal} ${config.entityNamePlural}`
                    : `Préparation...`}
                </p>
              </div>
            </div>
          )}

          {/* ── Mode: Import Result ── */}
          {mode === "import-result" && importResult && (
            <div className="p-5 space-y-4">
              {importResult.succeeded > 0 && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--color-olive-bg)] border border-[var(--color-olive-border)] text-[var(--color-olive)] text-sm">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  {importResult.succeeded} {config.entityNamePlural} importé(e)s avec succès
                </div>
              )}
              {importResult.failed.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--color-brick-bg)] border border-[var(--color-brick-border)] text-[var(--color-brick)] text-sm">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    {importResult.failed.length} {config.entityNamePlural} en échec
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {importResult.failed.map((f, i) => (
                      <div key={i} className="flex items-start gap-2 px-3 py-1.5 text-xs text-[var(--color-brick)] bg-[var(--color-brick-bg)]/50 rounded">
                        <span className="font-medium">{f.name} :</span>
                        <span>{f.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {importResult.autoCreatedSuppliers && importResult.autoCreatedSuppliers.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--color-amber-bg)] border border-[var(--color-amber-border)] text-[var(--color-amber)] text-sm">
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium">
                        {importResult.autoCreatedSuppliers.length} fournisseur{importResult.autoCreatedSuppliers.length > 1 ? "s" : ""} créé{importResult.autoCreatedSuppliers.length > 1 ? "s" : ""} automatiquement
                      </div>
                      <div className="text-xs opacity-80 mt-0.5">
                        Ces fournisseurs étaient référencés par les liens mais n'existaient pas. Pensez à compléter leurs informations de contact dans la section Fournisseurs.
                      </div>
                    </div>
                  </div>
                  <details className="rounded-lg border border-[var(--border)] overflow-hidden">
                    <summary className="px-3 py-2 text-xs text-[var(--muted-foreground)] cursor-pointer hover:bg-[var(--muted)]/50 select-none">
                      Voir la liste ({importResult.autoCreatedSuppliers.length})
                    </summary>
                    <div className="max-h-48 overflow-y-auto px-3 py-2 bg-[var(--muted)]/30">
                      <div className="flex flex-wrap gap-1.5">
                        {importResult.autoCreatedSuppliers.map((name, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md bg-[var(--color-amber-bg)] border border-[var(--color-amber-border)] text-[var(--color-amber)]"
                          >
                            <Plus className="w-3 h-3 opacity-70 flex-shrink-0" />
                            <span className="whitespace-nowrap">{name}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </details>
                </div>
              )}
              {importResult.autoCreatedResources && importResult.autoCreatedResources.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[var(--color-steel-bg)] border border-[var(--color-steel-border)] text-[var(--color-steel)] text-sm">
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium">
                        {importResult.autoCreatedResources.length} ressource{importResult.autoCreatedResources.length > 1 ? "s" : ""} créée{importResult.autoCreatedResources.length > 1 ? "s" : ""} automatiquement
                      </div>
                      <div className="text-xs opacity-80 mt-0.5">
                        Ces ingrédients étaient référencés mais n'existaient pas. Pensez à compléter leurs coûts dans la page Inventaire.
                      </div>
                    </div>
                  </div>
                  <details className="rounded-lg border border-[var(--border)] overflow-hidden">
                    <summary className="px-3 py-2 text-xs text-[var(--muted-foreground)] cursor-pointer hover:bg-[var(--muted)]/50 select-none">
                      Voir la liste ({importResult.autoCreatedResources.length})
                    </summary>
                    <div className="max-h-48 overflow-y-auto px-3 py-2 bg-[var(--muted)]/30">
                      <div className="flex flex-wrap gap-1.5">
                        {importResult.autoCreatedResources.map((name, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md bg-[var(--color-steel-bg)] border border-[var(--color-steel-border)] text-[var(--color-steel)]"
                          >
                            <Plus className="w-3 h-3 opacity-70 flex-shrink-0" />
                            <span className="whitespace-nowrap">{name}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </details>
                </div>
              )}
              <Button variant="secondary" size="sm" onClick={handleClose} fullWidth>
                Fermer
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function FilterBadge({
  label,
  count,
  active,
  onClick,
  variant,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  variant: "success" | "warning" | "neutral" | "info";
}) {
  const colors = {
    success: active
      ? "bg-[var(--color-olive)] text-white border-[var(--color-olive)]"
      : "bg-[var(--color-olive-bg)] text-[var(--color-olive)] border-[var(--color-olive-border)] hover:bg-[var(--color-olive)]/20",
    warning: active
      ? "bg-[var(--color-amber)] text-white border-[var(--color-amber)]"
      : "bg-[var(--color-amber-bg)] text-[var(--color-amber)] border-[var(--color-amber-border)] hover:bg-[var(--color-amber)]/20",
    info: active
      ? "bg-[var(--color-steel)] text-white border-[var(--color-steel)]"
      : "bg-[var(--color-steel-bg)] text-[var(--color-steel)] border-[var(--color-steel-border)] hover:bg-[var(--color-steel)]/20",
    neutral: active
      ? "bg-[var(--color-steel)] text-white border-[var(--color-steel)]"
      : "bg-[var(--muted)] text-[var(--muted-foreground)] border-[var(--border)] hover:bg-[var(--switch-background)]",
  };

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${colors[variant]}`}
    >
      {label}
      <span className={`${active ? "opacity-80" : "opacity-60"}`}>{count}</span>
    </button>
  );
}

function ImportItemRow<T>({
  item,
  allItems,
  config,
  isExpanded,
  onToggleExpand,
  onToggleSelect,
  onSetResolution,
  onChoosePrimary,
  importedAt,
  isGroupResolved,
  onConfirmGroup,
  onUndoGroup,
}: {
  item: ImportItem<T>;
  allItems: ImportItem<T>[];
  config: ImportExportConfig<T>;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onSetResolution: (res: "incoming" | "existing") => void;
  onChoosePrimary: (rowId: string) => void;
  isGroupResolved: boolean;
  onConfirmGroup: (groupKey: string) => void;
  onUndoGroup: (groupKey: string) => void;
  importedAt: string | null;
}) {
  const statusConfig = {
    new: {
      icon: Plus,
      label: "Nouveau",
      bg: "bg-[var(--color-olive-bg)]",
      text: "text-[var(--color-olive)]",
      border: "border-l-[var(--color-olive)]",
      badgeVariant: "success" as const,
    },
    skip: {
      icon: SkipForward,
      label: "Identique",
      bg: "bg-[var(--muted)]",
      text: "text-[var(--muted-foreground)]",
      border: "border-l-[var(--muted-foreground)]",
      badgeVariant: "neutral" as const,
    },
    update: {
      icon: RefreshCw,
      label: "Mise à jour",
      bg: "bg-[var(--color-steel-bg)]",
      text: "text-[var(--color-steel)]",
      border: "border-l-[var(--color-steel)]",
      badgeVariant: "success" as const,
    },
    conflict: {
      icon: AlertTriangle,
      label: "Conflit",
      bg: "bg-[var(--color-amber-bg)]",
      text: "text-[var(--color-amber)]",
      border: "border-l-[var(--color-amber)]",
      badgeVariant: "warning" as const,
    },
    duplicate: {
      icon: Copy,
      label: "Doublon",
      bg: "bg-[var(--color-amber-bg)]",
      text: "text-[var(--color-amber)]",
      border: "border-l-[var(--color-amber)]",
      badgeVariant: "warning" as const,
    },
  };

  const sc = statusConfig[item.status];
  const StatusIcon = sc.icon;
  const category = config.getCategory?.(item.data) || "";
  const hasGroup = item.groupSize > 1;
  // Conflict view: by default only show fields that differ between versions; toggle to see all
  const [showAllConflictFields, setShowAllConflictFields] = useState(false);

  // Sibling occurrences in this duplicate group (including self), in file order
  const groupKey = config.getKey(item.data).toLowerCase();
  const groupSiblings = hasGroup
    ? allItems.filter((it) => config.getKey(it.data).toLowerCase() === groupKey)
    : [];

  // Date for this incoming item — prefer per-item _createdAt (richer data),
  // fall back to the file's exportedAt (one date for the whole batch).
  const itemDate = config.getItemDate?.(item.data);
  const incomingDate = itemDate ? formatDate(itemDate) : (importedAt ? formatDate(importedAt) : null);
  const incomingDateSource: "item" | "file" | null = itemDate ? "item" : (importedAt ? "file" : null);
  // Date for the existing item in the system (if available)
  const existingDate = item.existing && config.getItemDate
    ? formatDate(config.getItemDate(item.existing))
    : null;

  // Whether this row should expose an expandable panel
  const canExpand = item.status === "conflict" || hasGroup || !!config.renderDetail;

  return (
    <div className={`border-b border-[var(--border)] border-l-4 ${sc.border}`}>
      {/* Main row */}
      <div className="flex items-center gap-3 px-5 py-3">
        {/* Checkbox */}
        {item.status !== "skip" && item.status !== "duplicate" ? (
          <button
            onClick={onToggleSelect}
            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
              item.selected
                ? "bg-[var(--color-steel)] border-[var(--color-steel)] text-white"
                : "border-[var(--switch-background)] hover:border-[var(--color-steel)]"
            }`}
          >
            {item.selected && <Check className="w-3 h-3" />}
          </button>
        ) : (
          <div className="w-5 h-5 flex-shrink-0" />
        )}

        {/* Status icon */}
        <div className={`p-1.5 rounded ${sc.bg} flex-shrink-0`}>
          <StatusIcon className={`w-3.5 h-3.5 ${sc.text}`} />
        </div>

        {/* Name + category */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[var(--foreground)] truncate flex items-center gap-2">
            {config.getDisplayName(item.data)}
          </div>
          <div className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] truncate">
            {category && <span>{category}</span>}
            {hasGroup && (
              <>
                {category && <span>·</span>}
                <span className="text-[var(--color-amber)]">
                  {item.groupSize} occurrences dans le fichier
                </span>
              </>
            )}
            {incomingDate && (
              <>
                <span>·</span>
                <span>
                  {incomingDateSource === "item" ? "Créé le" : "Du fichier daté du"} {incomingDate}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Compare button for groups — different style when group is resolved */}
        {hasGroup && (
          <button
            onClick={onToggleExpand}
            className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-colors ${
              isGroupResolved
                ? "border-[var(--color-olive-border)] bg-[var(--color-olive-bg)] text-[var(--color-olive)] hover:bg-[var(--color-olive)]/20"
                : "border-[var(--color-amber-border)] bg-[var(--color-amber-bg)] text-[var(--color-amber)] hover:bg-[var(--color-amber)]/20"
            }`}
            title={isGroupResolved ? "Doublon résolu — voir ou annuler" : "Comparer les doublons"}
          >
            {isGroupResolved ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {isGroupResolved ? "Résolu" : `Comparer (${item.groupSize})`}
          </button>
        )}

        {/* Status badge */}
        <Badge variant={sc.badgeVariant}>{sc.label}</Badge>

        {/* Expand button — for conflicts (comparison) or any item with detail renderer */}
        {canExpand && !hasGroup && (
          <button
            onClick={onToggleExpand}
            className="p-1 text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Expanded duplicate-group compare panel — shown when this row is in a group */}
      {hasGroup && isExpanded && (
        <div className="px-5 pb-4">
          <div className={`rounded-lg border overflow-hidden ${
            isGroupResolved ? "border-[var(--color-olive-border)]" : "border-[var(--color-amber-border)]"
          }`}>
            <div className={`px-3 py-2 text-xs font-medium border-b ${
              isGroupResolved
                ? "bg-[var(--color-olive-bg)] text-[var(--color-olive)] border-[var(--color-olive-border)]"
                : "bg-[var(--color-amber-bg)] text-[var(--color-amber)] border-[var(--color-amber-border)]"
            }`}>
              {isGroupResolved
                ? `Choix confirmé — ${item.groupSize} occurrences trouvées dans le fichier`
                : `${item.groupSize} occurrences trouvées dans le fichier — choisir la version à importer`}
            </div>
            <div className="divide-y divide-[var(--border)]">
              {groupSiblings.map((sibling) => (
                <DuplicateCompareCard
                  key={sibling.rowId}
                  sibling={sibling}
                  config={config}
                  isPrimary={sibling.isPrimary}
                  isCurrentRow={sibling.rowId === item.rowId}
                  importedAt={importedAt}
                  onChoose={() => onChoosePrimary(sibling.rowId)}
                  isGroupResolved={isGroupResolved}
                />
              ))}
            </div>
            {/* Footer: confirm or undo the resolution */}
            <div className="flex justify-end gap-2 p-3 border-t border-[var(--border)] bg-[var(--muted)]/50">
              {isGroupResolved ? (
                <button
                  onClick={() => onUndoGroup(config.getKey(item.data).toLowerCase())}
                  className="px-3 py-1.5 text-xs font-medium rounded-md border border-[var(--switch-background)] text-[var(--muted-foreground)] hover:border-[var(--color-amber)] hover:text-[var(--color-amber)] transition-colors"
                >
                  Annuler la confirmation
                </button>
              ) : (
                <button
                  onClick={() => onConfirmGroup(config.getKey(item.data).toLowerCase())}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--color-olive)] text-white hover:bg-[var(--color-olive)]/90 transition-colors"
                >
                  <Check className="w-3.5 h-3.5" />
                  Confirmer ce choix
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expanded detail for non-conflict, non-grouped items (e.g., ingredient preview) */}
      {!hasGroup && item.status !== "conflict" && isExpanded && config.renderDetail && (
        <div className="px-5 pb-3">
          <div className="rounded-lg border border-[var(--border)] bg-[var(--muted)]/30 p-3 text-xs">
            {config.renderDetail(item.data)}
          </div>
        </div>
      )}

      {/* Expanded comparison for conflicts. Shown when item is a conflict and either:
          (a) not in a duplicate group, or (b) is currently the primary occurrence (so resolution applies). */}
      {item.status === "conflict" && isExpanded && item.existing && (!hasGroup || item.isPrimary) && (() => {
        // Compute which fields actually differ — by default only show those (compact view).
        const fieldsWithDiff = config.comparisonFields.map((field) => {
          const incomingVal = getNestedValue(item.data, field.key);
          const existingVal = getNestedValue(item.existing, field.key);
          const formatted = field.format || ((v: any) => v?.toString() ?? "—");
          const isDifferent = formatted(incomingVal) !== formatted(existingVal);
          return { field, incomingVal, existingVal, formatted, isDifferent };
        });
        const changedFields = fieldsWithDiff.filter((f) => f.isDifferent);
        const identicalCount = fieldsWithDiff.length - changedFields.length;
        const visibleFields = showAllConflictFields ? fieldsWithDiff : changedFields;

        return (
          <div className="px-5 pb-4">
            <div className="rounded-lg border border-[var(--color-amber-border)] overflow-hidden">
              {/* Compact header with diff summary + toggle */}
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[var(--color-amber-bg)] text-xs border-b border-[var(--color-amber-border)]">
                <span className="text-[var(--color-amber)] font-medium">
                  {changedFields.length === 0
                    ? "Aucune différence (les versions sont identiques)"
                    : `${changedFields.length} champ${changedFields.length > 1 ? "s" : ""} différent${changedFields.length > 1 ? "s" : ""}`}
                  {identicalCount > 0 && !showAllConflictFields && (
                    <span className="opacity-75 font-normal"> · {identicalCount} identique{identicalCount > 1 ? "s" : ""} masqué{identicalCount > 1 ? "s" : ""}</span>
                  )}
                </span>
                {identicalCount > 0 && (
                  <button
                    onClick={() => setShowAllConflictFields((s) => !s)}
                    className="text-[var(--color-amber)] hover:underline text-[11px]"
                  >
                    {showAllConflictFields ? "Masquer les identiques" : "Voir tous les champs"}
                  </button>
                )}
              </div>

              {/* Mini date row — incoming vs existing dates side by side */}
              {(incomingDate || (existingDate && existingDate !== "—")) && (
                <div className="flex text-[10px] text-[var(--muted-foreground)] border-b border-[var(--border)] divide-x divide-[var(--border)]">
                  <div className="flex-1 px-3 py-1 text-[var(--color-amber)]">
                    {incomingDate ? (
                      incomingDateSource === "item"
                        ? `Importé · créé le ${incomingDate}`
                        : `Importé · fichier daté du ${incomingDate}`
                    ) : "Importé"}
                  </div>
                  <div className="flex-1 px-3 py-1 text-[var(--color-steel)]">
                    {existingDate && existingDate !== "—" ? `Actuel · créé le ${existingDate}` : "Actuel"}
                  </div>
                </div>
              )}

              {/* Field rows — only those that differ by default */}
              {visibleFields.map(({ field, incomingVal, existingVal, formatted, isDifferent }) => {
                // Custom diff renderer (e.g. ingredient diff) — full-width
                if (field.renderDiff && isDifferent) {
                  return (
                    <div key={field.key} className="border-t border-[var(--border)] bg-[var(--color-amber-bg)]/20">
                      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-[var(--muted-foreground)] font-medium">
                        {field.label}
                      </div>
                      <div className="px-3 pb-2">
                        {field.renderDiff(incomingVal, existingVal)}
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={field.key}
                    className={`grid grid-cols-[120px,1fr,1fr] text-xs border-t border-[var(--border)] ${
                      isDifferent ? "bg-[var(--color-amber-bg)]/20" : ""
                    }`}
                  >
                    <div className="px-3 py-1.5 text-[var(--muted-foreground)] font-medium">
                      {field.label}
                    </div>
                    <div className={`px-3 py-1.5 border-l border-[var(--border)] ${
                      isDifferent ? "text-[var(--color-amber)] font-medium" : "text-[var(--foreground)]"
                    }`}>
                      {formatted(incomingVal)}
                    </div>
                    <div className={`px-3 py-1.5 border-l border-[var(--border)] ${
                      isDifferent ? "text-[var(--color-steel)] font-medium" : "text-[var(--foreground)]"
                    }`}>
                      {formatted(existingVal)}
                    </div>
                  </div>
                );
              })}

              {/* Compact resolution buttons (inline pills) */}
              <div className="flex items-center gap-2 px-3 py-2 border-t border-[var(--border)] bg-[var(--muted)]/40">
                <span className="text-[11px] text-[var(--muted-foreground)] mr-auto">Conserver :</span>
                <button
                  onClick={() => onSetResolution("incoming")}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                    item.resolution === "incoming"
                      ? "bg-[var(--color-amber)] text-white border-[var(--color-amber)]"
                      : "border-[var(--switch-background)] text-[var(--muted-foreground)] hover:border-[var(--color-amber)] hover:text-[var(--color-amber)]"
                  }`}
                >
                  <Upload className="w-3 h-3" />
                  Version importée
                </button>
                <button
                  onClick={() => onSetResolution("existing")}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                    item.resolution === "existing"
                      ? "bg-[var(--color-steel)] text-white border-[var(--color-steel)]"
                      : "border-[var(--switch-background)] text-[var(--muted-foreground)] hover:border-[var(--color-steel)] hover:text-[var(--color-steel)]"
                  }`}
                >
                  <Check className="w-3 h-3" />
                  Version actuelle
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** Card shown for each occurrence inside a duplicate-group compare panel */
function DuplicateCompareCard<T>({
  sibling,
  config,
  isPrimary,
  isCurrentRow,
  importedAt,
  onChoose,
  isGroupResolved,
}: {
  sibling: ImportItem<T>;
  config: ImportExportConfig<T>;
  isPrimary: boolean;
  isCurrentRow: boolean;
  importedAt: string | null;
  onChoose: () => void;
  isGroupResolved: boolean;
}) {
  const category = config.getCategory?.(sibling.data) || "";
  const itemDate = config.getItemDate?.(sibling.data);
  const incomingDate = itemDate ? formatDate(itemDate) : (importedAt ? formatDate(importedAt) : null);
  const incomingDateLabel = itemDate ? "Créé le" : "Fichier daté du";

  // Dim non-primary siblings once the group is resolved — visually pushes the user
  // toward the chosen one without losing the rest of the context.
  const dim = isGroupResolved && !isPrimary;
  const [showDetail, setShowDetail] = useState(false);
  const mediumBody = config.renderMedium?.(sibling.data) ?? config.renderSummary?.(sibling.data);
  const hasFullDetail = !!config.renderDetail && config.renderMedium !== config.renderDetail;

  return (
    <div className={`px-3 py-2 transition-opacity ${
      isPrimary ? "bg-[var(--color-olive-bg)]/30" : "bg-[var(--card)]"
    } ${dim ? "opacity-50" : ""}`}>
      {/* Header row — badges and actions only; summary moved to body */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-[var(--muted)] text-[var(--muted-foreground)] flex-shrink-0">
          #{sibling.groupIndex + 1}
        </span>
        {isPrimary && (
          <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-[var(--color-olive)] text-white flex-shrink-0">
            {isGroupResolved ? "Confirmée" : "Choisie"}
          </span>
        )}
        {isCurrentRow && !isPrimary && (
          <span className="text-[11px] text-[var(--muted-foreground)] italic flex-shrink-0">(ligne actuelle)</span>
        )}
        {category && <span className="text-[11px] text-[var(--muted-foreground)] flex-shrink-0">{category}</span>}
        {/* Date pushed to the right */}
        <span className="ml-auto flex items-center gap-2">
          {incomingDate && (
            <span className="text-[10px] text-[var(--muted-foreground)] flex-shrink-0">
              {incomingDateLabel} {incomingDate}
            </span>
          )}
          {hasFullDetail && (
            <button
              onClick={() => setShowDetail((s) => !s)}
              className="p-0.5 text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0"
              title={showDetail ? "Masquer le détail complet" : "Voir le détail complet"}
            >
              {showDetail ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          {isPrimary ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-[var(--color-olive)] text-white flex-shrink-0">
              <Check className="w-3 h-3" />
              {isGroupResolved ? "Confirmée" : "Sélectionnée"}
            </span>
          ) : (
            <button
              onClick={onChoose}
              disabled={isGroupResolved}
              title={isGroupResolved ? "Annuler la confirmation pour changer de version" : undefined}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md border border-[var(--switch-background)] text-[var(--muted-foreground)] hover:border-[var(--color-olive)] hover:text-[var(--color-olive)] hover:bg-[var(--color-olive-bg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[var(--switch-background)] disabled:hover:text-[var(--muted-foreground)] disabled:hover:bg-transparent flex-shrink-0"
            >
              Choisir
            </button>
          )}
        </span>
      </div>

      {/* Body: mid-density view (icons + grouped names) — always visible */}
      {mediumBody && (
        <div className="pl-1">
          {mediumBody}
        </div>
      )}

      {/* Expandable full detail (renderDetail with quantities/costs table) */}
      {showDetail && hasFullDetail && (
        <div className="mt-2 rounded border border-[var(--border)] bg-[var(--muted)]/30 p-2 text-xs">
          {config.renderDetail!(sibling.data)}
        </div>
      )}
    </div>
  );
}
